import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  OrderDeliveryType,
  OrderMKTPDeliveryMode,
} from '@1po/1po-bff-fe-spec/generated/order/request/CheckoutOrderRequest';
import { ExadisPickUpIcon } from 'assets/icons';
import PrivateComponent from 'composers/PrivateComponent';
import { DEALER_PICKUP, OWN_ADDRESS } from 'domains/basket/Basket.types';
import { SectionCard, SText } from 'pages/CartPage/ValidationStep/DeliveryAddressSection.styled';
import ModifyButton from 'pages/CartPage/ValidationStep/ModifyButton';
import { theme } from 'styles';
import { Box, Flex, Icon, PaddingBox, Radio, RadioGroup, Space, Switch, Text } from 'UI';
import { Country } from 'utils/i18n/Country';

interface DeliveryAddressSectionProps {
  validationDeliveryAddress: OrderDeliveryType;
  updateDeliveryAddress: (value: OrderDeliveryType) => void;
  orderMKTPDeliveryMode: OrderMKTPDeliveryMode | undefined;
  basketHasMKTPReference: boolean;
  updateOrderMKTPDeliveryMode: (value: OrderMKTPDeliveryMode) => void;
  sellerName?: string;
  compact?: boolean;
  updateExadisDeliveryMode?: (value: boolean) => void;
  basketIAMReferencesCount?: number;
  basketExadisDeliveryMode?: boolean;
  isDealerPickupEnabled: boolean;
  allowMktpDeliveryOptions: boolean;
}

const DeliveryAddressSection = ({
  validationDeliveryAddress,
  updateDeliveryAddress,
  orderMKTPDeliveryMode,
  basketHasMKTPReference,
  updateOrderMKTPDeliveryMode,
  sellerName = '',
  compact = false,
  updateExadisDeliveryMode,
  basketIAMReferencesCount,
  basketExadisDeliveryMode = false,
  isDealerPickupEnabled,
  allowMktpDeliveryOptions,
}: DeliveryAddressSectionProps) => {
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState<boolean>(false);
  const displayExadisOption = !!basketIAMReferencesCount && updateExadisDeliveryMode;
  useEffect(() => {
    setEditMode(false);
  }, [validationDeliveryAddress]);

  return (
    <Flex direction={'column'} data-cy={'section-delivery-options'} gap={compact ? 10 : 30}>
      <SectionCard direction={'row'} align={'center'} editMode={editMode}>
        <Flex direction={'column'} alignSelf={'flex-start'}>
          <Text type={'h4_paragraph'} data-cy={'title-delivery-address'}>
            {t('order.validation.delivery.title', '1. Delivery address')}
          </Text>
          <Box height={10} />
          {!editMode && (
            <>
              <Text type="base" data-cy={`delivery-method`}>
                {validationDeliveryAddress === OWN_ADDRESS ? (
                  <span data-cy={'deliver-to-my-address'}>
                    {t('order.validation.address.options.my_address', 'Deliver to my address')}
                  </span>
                ) : (
                  <span data-cy={'dealer-pick-up'}>
                    <span data-cy={'dealer-pick-up-text'}>
                      {t('order.validation.address.options.dealer_address', 'Dealer pick-up at:')}
                    </span>{' '}
                    <span data-cy={'seller-name'}>{sellerName}</span>
                  </span>
                )}
              </Text>
              {basketHasMKTPReference && (
                <Text type="base">
                  {orderMKTPDeliveryMode === 'STANDARD'
                    ? t('order.validation.delivery.options.standard', 'Standard delivery - 10,00€')
                    : t('order.validation.delivery.options.multiple', 'Multiple deliveries - 10,00€ per delivery')}
                </Text>
              )}
            </>
          )}
          <Text type="base" color="grey35">
            {t(
              'order.validation.address.description',
              'The selected delivery options will apply by default to your entire order.',
            )}
          </Text>
          {editMode && (
            <>
              {!compact && <Box height={30} />}
              <RadioGroup value={validationDeliveryAddress}>
                <Space direction={'vertical'} size={15}>
                  <Radio
                    value={OWN_ADDRESS}
                    onChange={() => updateDeliveryAddress(OWN_ADDRESS)}
                    label={
                      <Text type="base" color="grey35" cursor={'pointer'} data-cy={'deliver-to-my-address-method'}>
                        {t('order.validation.address.options.my_address', 'Deliver to my address')}
                      </Text>
                    }
                  />
                  {isDealerPickupEnabled && (
                    <Radio
                      value={DEALER_PICKUP}
                      onChange={() => updateDeliveryAddress(DEALER_PICKUP)}
                      label={
                        <Text type="base" color="grey35" cursor={'pointer'} data-cy={'dealer-pickup-method'}>
                          {`${t(
                            'order.validation.address.options.dealer_address',
                            'Dealer pick-up at:',
                          )} ${sellerName}`}
                        </Text>
                      }
                    />
                  )}
                </Space>
              </RadioGroup>
              {displayExadisOption && (
                <PrivateComponent
                  render={() => (
                    <>
                      <Box height={30} />
                      <Text type="base" color="grey35">
                        {t(
                          'order.validation.exadis_pickup.description',
                          /* eslint-disable max-len */
                          'Enable the option to collect available parts at the nearest EXADIS counter. Parts that are not available in sufficient quantity will be delivered according to the selected delivery method.',
                        )}
                      </Text>
                      <PaddingBox py={10}>
                        <Flex direction={'row'} maxWidth={180} align={'center'} gap={10} data-cy={'exadis-pickup'}>
                          <Icon
                            IconComponent={ExadisPickUpIcon}
                            color={theme.color.brand_black}
                            width={150}
                            height={35}
                            data-cy={'exadis-pickup-logo'}
                          />
                          <Switch
                            onChange={() => updateExadisDeliveryMode(!basketExadisDeliveryMode)}
                            checked={basketExadisDeliveryMode}
                            data-cy={'exadis-pickup-switch'}
                          />
                        </Flex>
                      </PaddingBox>
                      <Text type="base" color="grey35" data-cy={'count-items-available-exadis'}>
                        {t(
                          'order.validation.address.exadis.description',
                          '{{count}} items available for EXADIS PICK_UP will be available at your closest Exadis counter.',
                          { count: basketIAMReferencesCount },
                        )}
                      </Text>
                    </>
                  )}
                  requiredCountries={[Country.FR]}
                />
              )}
              {basketHasMKTPReference && allowMktpDeliveryOptions && (
                <PaddingBox px={32} py={32}>
                  <MKTPDelivery
                    orderMKTPDeliveryMode={orderMKTPDeliveryMode}
                    updateOrderMKTPDeliveryMode={updateOrderMKTPDeliveryMode}
                  />
                </PaddingBox>
              )}
            </>
          )}
        </Flex>
        {(isDealerPickupEnabled || displayExadisOption) && (
          <ModifyButton editMode={editMode} handleEdit={setEditMode} />
        )}
      </SectionCard>
    </Flex>
  );
};

interface MKTPDeliveryProps {
  orderMKTPDeliveryMode: OrderMKTPDeliveryMode | undefined;
  updateOrderMKTPDeliveryMode: (value: OrderMKTPDeliveryMode) => void;
}

function MKTPDelivery(props: MKTPDeliveryProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (props.orderMKTPDeliveryMode === undefined) {
      props.updateOrderMKTPDeliveryMode('STANDARD');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Text type={'h4_paragraph'}>
        {t('order.validation.delivery.description', 'Shipping cost for other brand parts')}
      </Text>
      <Box height={15} />
      <RadioGroup
        onChange={(event) => props.updateOrderMKTPDeliveryMode(event.target.value)}
        value={props.orderMKTPDeliveryMode}
      >
        <Flex direction={'column'}>
          <Radio
            value={'STANDARD'}
            label={
              <Text type="lg" bold cursor={'pointer'}>
                {t('order.validation.delivery.options.standard', 'Standard delivery - 10,00€')}
              </Text>
            }
          />
          <SText type={'light_14_black_65'}>
            {t('order.validation.delivery.options.standard.description', 'Receive all your items at once')}
          </SText>
          <Box height={30} />
          <Radio
            value={'MULTIPLE'}
            label={
              <Text type="lg" bold cursor={'pointer'}>
                {t('order.validation.delivery.options.multiple', 'Multiple deliveries - 10,00€ per delivery')}
              </Text>
            }
          />
          <SText type={'light_14_black_65'}>
            {t(
              'order.validation.delivery.options.multiple.description',
              'Receive your items as soon they are available',
            )}
          </SText>
        </Flex>
      </RadioGroup>
    </>
  );
}

export default DeliveryAddressSection;
